/**
 * `normalizeText` — the single normalisation function used at BOTH index time and query
 * time (`docs/spec.md` «Поиск»). If these two ever diverge, recall silently halves on
 * exactly the Russian titles this catalogue is full of, so there is one implementation
 * and it lives here.
 *
 * The pipeline, in order:
 *
 *   1. fold the special letters that NFKD does NOT decompose
 *      (`ё→е`, `Ё→Е`, `ß→ss`, `ẞ→SS`, `ø/Ø→o`, `æ/Æ→ae`, `ł/Ł→l`, `đ/Đ→d`)
 *   2. turn U+00A0 (NBSP) and U+202F (narrow NBSP) into a plain space — explicitly, and
 *      BEFORE the punctuation collapse (`docs/spec.md` «Поиск»)
 *   3. `NFKD`
 *   4. strip every combining mark (`\p{M}+`)
 *   5. lowercase
 *   6. fold again — the repair pass, see below
 *   7. replace every run of non-`\p{L}\p{N}` with a single space
 *   8. trim
 *
 * ## Why the fold runs twice
 *
 * Step 1 alone does not make the function idempotent, which acceptance criterion 1
 * requires. Two real counter-examples:
 *
 *   - `Ǿ` (U+01FE) is not in the fold table. NFKD decomposes it to `Ø` + U+0301; the mark
 *     strip leaves a bare `Ø`, which lowercases to `ø` — a letter, so the punctuation
 *     collapse keeps it. A second pass would then fold it to `o`. Same story for `ǽ`,
 *     `Ǣ`, `Ḑ`-style composites over a folded base.
 *   - `ẞ` (U+1E9E) survives NFKD untouched and `toLowerCase()` turns it into `ß`, which a
 *     second pass would fold to `ss`. (`ẞ` is in the fold table for belt-and-braces, but
 *     the repair pass is what makes the class of cases safe rather than the enumeration.)
 *
 * Running the fold once more after the lowercase step closes the whole class: after step 6
 * none of the folded code points can be present, and steps 7–8 cannot reintroduce them.
 */
/**
 * Letters NFKD will not take apart for us. Both cases are listed so the function stays
 * case-preserving right up to the lowercase step; the uppercase rows still matter because
 * the repair pass (step 6) runs on already-lowercased text and reuses this same table.
 */
const FOLD_PAIRS = [
    ['ё', 'е'],
    ['Ё', 'Е'],
    ['ß', 'ss'],
    ['ẞ', 'SS'],
    ['ø', 'o'],
    ['Ø', 'O'],
    ['æ', 'ae'],
    ['Æ', 'AE'],
    ['ł', 'l'],
    ['Ł', 'L'],
    ['đ', 'd'],
    ['Đ', 'D'],
];
const FOLD_TABLE = new Map(FOLD_PAIRS);
/** `[ёЁßẞøØæÆłŁđĐ]` built from the table so the two can never drift apart. */
const FOLD_RE = new RegExp(`[${FOLD_PAIRS.map(([from]) => from).join('')}]`, 'gu');
/** NBSP and NARROW NO-BREAK SPACE. */
const HARD_SPACE_RE = /[\u00A0\u202F]/gu;
const COMBINING_MARK_RE = /\p{M}+/gu;
const NON_ALNUM_RUN_RE = /[^\p{L}\p{N}]+/gu;
function foldSpecials(input) {
    return input.replace(FOLD_RE, (ch) => FOLD_TABLE.get(ch) ?? ch);
}
/**
 * Normalise a string for indexing and for querying.
 *
 * Idempotent: `normalizeText(normalizeText(x)) === normalizeText(x)` for every input.
 * The result contains only lowercase `\p{L}`/`\p{N}` characters separated by single
 * spaces, with no leading or trailing space. An input with no letters or digits at all
 * (`'   '`, `',,,'`, `'🙂'`) normalises to the empty string — callers such as the FTS5
 * query builder treat that as "no tokens", not as an error.
 */
export function normalizeText(input) {
    if (input === '')
        return '';
    return foldSpecials(foldSpecials(input)
        .replace(HARD_SPACE_RE, ' ')
        .normalize('NFKD')
        .replace(COMBINING_MARK_RE, '')
        .toLowerCase())
        .replace(NON_ALNUM_RUN_RE, ' ')
        .trim();
}
/**
 * Split normalised text into tokens. Convenience for the callers that immediately want
 * the token list (`buildSearchKey`, the FTS5 query builder); it never yields an empty
 * token, so `tokens.length === 0` is the "nothing searchable here" signal.
 */
export function normalizeTokens(input) {
    const normalized = normalizeText(input);
    return normalized === '' ? [] : normalized.split(' ');
}
//# sourceMappingURL=normalize.js.map